import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, Package, Heart, LogOut } from "lucide-react";
import { api, apiError } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { WILAYAS } from "../utils/wilayas";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const inputCls = "w-full bg-white border border-[#EAE8E4] rounded-xl px-4 py-3 text-sm text-[#111111] outline-none focus:border-[#111111] transition-colors font-body";

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    full_name: user?.full_name || "", phone: user?.phone || "", wilaya: user?.wilaya || "", address: user?.address || "",
  });
  const [pwd, setPwd] = useState({ old_password: "", new_password: "", confirm: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const initials = (user?.full_name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/auth/me", profile);
      await refreshUser();
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setSavingPwd(true);
    try {
      await api.put("/auth/change-password", { old_password: pwd.old_password, new_password: pwd.new_password });
      toast.success("Mot de passe modifié");
      setPwd({ old_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="container-queen pt-32 pb-24" id="main">
      <div className="flex items-center gap-5 mb-12">
        <div className="w-16 h-16 rounded-full bg-[#111111] text-white font-display text-2xl grid place-items-center shadow-md">
          {initials}
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#111111]">{user?.full_name}</h1>
          <p className="text-xs text-[#888888] font-body mt-1">{user?.email}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <form onSubmit={saveProfile} className="bg-white rounded-3xl border border-[#EAE8E4] p-8 space-y-4">
          <h2 className="font-display text-2xl text-[#111111] mb-6">Informations Personnelles</h2>
          <div>
            <label className="label-eyebrow text-[#666666] block mb-2">Nom complet</label>
            <input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#666666] block mb-2">Téléphone</label>
            <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#666666] block mb-2">Wilaya par défaut</label>
            <Select value={profile.wilaya} onValueChange={(v) => setProfile({ ...profile, wilaya: v })}>
              <SelectTrigger className="border border-[#EAE8E4] rounded-xl h-12"><SelectValue placeholder="Sélectionnez votre wilaya" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {WILAYAS.map((w) => <SelectItem key={w.code} value={w.name}>{w.code} — {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-eyebrow text-[#666666] block mb-2">Adresse</label>
            <textarea value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} rows={2} className={inputCls} />
          </div>
          <button type="submit" disabled={savingProfile} className="btn-queen-primary w-full h-12 text-xs uppercase tracking-wider mt-4">
            {savingProfile ? "Enregistrement..." : "Enregistrer les modifications"}
          </button>
        </form>

        <form onSubmit={changePassword} className="bg-white rounded-3xl border border-[#EAE8E4] p-8 space-y-4">
          <h2 className="font-display text-2xl text-[#111111] mb-6">Sécurité &amp; Mot de passe</h2>
          <div>
            <label className="label-eyebrow text-[#666666] block mb-2">Ancien mot de passe</label>
            <input type="password" value={pwd.old_password} onChange={(e) => setPwd({ ...pwd, old_password: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#666666] block mb-2">Nouveau mot de passe</label>
            <input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="label-eyebrow text-[#666666] block mb-2">Confirmer le mot de passe</label>
            <input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className={inputCls} />
          </div>
          <button type="submit" disabled={savingPwd} className="btn-queen-secondary w-full h-12 text-xs uppercase tracking-wider mt-4">
            {savingPwd ? "Mise à jour..." : "Mettre à jour le mot de passe"}
          </button>
        </form>
      </div>
    </div>
  );
}
